
































































































































































import { IEventUIDto } from "@/lib/dto/event/event-ui.dto";
import { IDialog } from "@/lib/interfaces/dialog.interface";
import { simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { handleError } from "@/lib/utility/handleError";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { IReport } from "@/models/report.entity";
import { ITicket, Ticket } from "@/models/ticket.entity";
import { MrfiktivReferenceGen } from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum } from "@/store/enum/authResourceEnum";
import { PartnerModule } from "@/store/modules/partner";
import { ProjectModule } from "@/store/modules/project.store";
import { RefTypeMap } from "@/store/modules/refs.store";
import { ProjectGoToHelper } from "@/views/project/mixins/ProjectGoToMixin.vue";
import { mixins } from "vue-class-component";
import { Component, Prop } from "vue-property-decorator";
import TicketRefsSelect from "../project/TicketRefsSelect.vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import TemplateEditor from "../template/TemplateEditor.vue";
import ProjectCreateDialog from "./project/ProjectCreateDialog.vue";
import SelectAssignees from "../utility/SelectAssignees.vue";
import SelectTags from "@/components/tag/v2/SelectTags.vue";
import { IVehicle } from "@/models/vehicle.entity";
import CustomFieldListForm from "@/components/report/CustomFieldListForm.vue";
import { ITag } from "@/models/tag.entity";
import { UserModule } from "@/store/modules/me-user.store";

type ElementType = IVehicle | IEventUIDto | IReport | ITicket;

@Component({
  components: {
    ConfirmActionDialog,
    TicketRefsSelect,
    TemplateEditor,
    SelectAssignees,
    SelectTags,
    ProjectCreateDialog,
    CustomFieldListForm
  },
  filters: {
    simpleDoubleDigitDate
  }
})
export default class TicketCreateDialog extends mixins(PermissionMixin, RulesMixin) implements IDialog {
  @Prop()
  projectId?: string;

  @Prop()
  partnerId!: string;

  @Prop()
  smallBtn!: boolean;

  @Prop()
  private isDialogActive!: boolean;

  @Prop()
  hideButton?: boolean;

  @Prop()
  isIconButton?: boolean;

  @Prop({ default: () => [] })
  refs!: MrfiktivReferenceGen[];

  @Prop({ default: false })
  hideToast!: boolean;

  @Prop()
  hideProject!: boolean;

  @Prop({ default: () => [] })
  tags!: ITag[];

  isLoadingProjects = false;

  isLoadingStartDialog = false;

  isLoading = false;

  ticket = new Ticket({
    projectId: this.projectId,
    refs: this.refs,
    tagIds: this.tags?.map(tag => tag.id) || [],
    tags: this.tags || [],
    assignees: [this.userId]
  });

  get userId() {
    return UserModule.userId;
  }

  get suggestions() {
    const suggestions: string[] = [];

    for (const suggestion of this.titleSuggestions ?? []) {
      if (!this.ticket.title.includes(suggestion) && suggestions.length < 3) {
        suggestions.push(suggestion);
      }
    }

    return suggestions;
  }

  get titleSuggestions() {
    const suggestions: string[] = [];
    if (!this.ticket.refs?.length) {
      return suggestions;
    }
    for (const ref of this.ticket.refs) {
      const module = RefTypeMap.get(ref.refType as BackendResourceEnum)?.module;
      let element: ElementType | undefined;
      if (module?.maps?.id) {
        element = module?.maps?.id.get(ref.refId)?.[0];
      }
      if (!element) {
        element = module?.entities?.find((el: ElementType) => el.id === ref.refId);
      }
      if (element) {
        suggestions.push(this.getTitleSuggestion(ref.refType as BackendResourceEnum, element));
      }
    }
    return suggestions;
  }

  getTitleSuggestion(refType: BackendResourceEnum, element: ElementType) {
    const prefix = this.$t(`BackendResourceEnum.${refType}`);
    switch (refType) {
      case BackendResourceEnum.VEHICLE: {
        const v = element as IVehicle;
        return `${this.$t("common.nouns.vehicle")}: ${v.displayName ?? v.numberplate}`;
      }
      case BackendResourceEnum.EVENT: {
        const e = element as IEventUIDto;
        return `${this.$t("common.nouns.appointment")}: ${e.summary}`;
      }
      case BackendResourceEnum.REPORT: {
        const r = element as IReport;
        return `${prefix}: ${r.title}`;
      }
      case BackendResourceEnum.TICKET: {
        const t = element as ITicket;
        return `${prefix}: #${t.number} ${t.title}`;
      }
      default: {
        return "";
      }
    }
  }

  get projects() {
    return ProjectModule.paginationList;
  }

  get dialogActive() {
    return this.isDialogActive;
  }

  set dialogActive(v: boolean) {
    this.$emit("update:isDialogActive", v);
  }

  get selectedProject() {
    const selectedId = this.ticket.projectId;
    if (!selectedId) {
      return undefined;
    }

    return ProjectModule._data.get(selectedId);
  }

  close() {
    this.dialogActive = false;
    this.$emit("close");
  }

  async refreshProjects() {
    this.isLoadingProjects = true;
    try {
      ProjectModule.setFilter([]);
      ProjectModule.setHiddenFilter([]);
      await ProjectModule.fetchAll({ partnerId: PartnerModule.partner.id });
    } catch (e) {
      handleError(e);
    }
    this.isLoadingProjects = false;
  }

  startDialog() {
    this.isLoadingStartDialog = true;

    this.ticket = new Ticket({
      projectId: this.projectId,
      refs: this.refs,
      tagIds: this.tags?.map(tag => tag.id) || [],
      tags: this.tags || [],
      assignees: [this.userId]
    });

    this.dialogActive = true;
    this.isLoadingStartDialog = false;
  }

  /**
   * Method to create ticket
   */
  async onCreateTicket() {
    if (!this.ticket) return;

    this.isLoading = true;

    this.ticket.partnerId = this.$route.params.partnerId;
    const created = await this.ticket.create();

    this.isLoading = false;

    if (!created) return;

    this.ticket = new Ticket({
      projectId: this.projectId,
      refs: this.refs,
      tagIds: this.tags?.map(tag => tag.id) || [],
      tags: this.tags || [],
      assignees: [this.userId]
    });

    this.dialogActive = false;

    this.$emit("created", created);

    if (!this.hideToast) {
      this.$toast.success(this.$t("project.ticket.created", { number: created.number }).toString(), {
        onClick: () => {
          created.goTo(this.$router).detail();
        }
      });
    }
  }

  open() {
    this.startDialog();
  }

  goToProject() {
    if (!this.ticket.projectId) {
      return;
    }

    new ProjectGoToHelper(this.$router).go(
      ProjectGoToHelper.locations.projectCustomView(
        PartnerModule.partner.id,
        this.ticket.projectId,
        "0",
        this.ticket.id
      )
    );
  }
}
